import React, {
  createContext,
  useState,
  useRef,
  useEffect,
  useMemo,
} from "react";
import { get } from "lodash";
import { locale, updateLocaleOptions, addLocale } from "primereact/api";
import { setLocale } from "yup";
import { useTranslation } from 'react-i18next';
import { Toast } from "primereact/toast";
import { ConfirmDialog } from "primereact/confirmdialog";
import { Dialog } from "primereact/dialog";
import { Sidebar } from "primereact/sidebar";
import { Carousel } from "primereact/carousel";
import { Avatar } from "primereact/avatar";
import { Button } from "primereact/button";
import { ProgressSpinner } from "primereact/progressspinner";
import { useNavigate } from "react-router-dom";
import { Image } from "components/ImageViewer";
import useMenus from "hooks/useMenus";
import useApi from "hooks/useApi";
import useAuth from "hooks/useAuth";
import { i18n } from "hooks/i18n";
import useUtils from "hooks/useUtils";

export const AppContext = createContext();

export const AppProvider = ({ children }) => {
  const { i18n } = useTranslation();
  const toast = useRef(null);
  const api = useApi();
  const auth = useAuth();
  const {t}=useTranslation();
  const [selectedOrg, setSelectedOrg] = useState(null);
  const [eventFilterParameters, setEventFilterParameters] = useState("");
  const [modules, setModules] = useState([]);
  const [metricFilterParameters, setMetricFilterParameters] = useState("");
  const [actionFilterParameters, setActionFilterParameters] = useState("");
  const [userFilterParameters, setUserFilterParameters] = useState("");
  const [modulesInProgress, setModulesInProgress] = useState(false);
  const [organisations, setOrganisations] = useState([]);
  const [allSettings, setAllSettings] = useState([]);
  const [theme, setTheme] = useState({});
  const [language, setLanguage] = useState({});
  const [roles, setRoles] = useState([]);
  const [actions, setActions] = useState([]);
  const [licenseData, setLicenseData] = useState({});
  const getModulesByOrgURL = `modules/get_all_modules/org_code/${selectedOrg?.code}`;
  useEffect(() => {
    const getRoles = `roles/get_roles`;
    getAllOrganisations();
    api
      .get(getRoles)
      .then((res) => setRoles(get(res, "data.data", [])))
      .catch((e) => setRoles([]));
    getAllModules();
    // const getActions = `actions/get_all_actions`;
    // api
    //   .get(getActions)
    //   .then((res) => setActions(get(res, "data.data", [])))
    //   .catch((e) => setActions([]));
    getAllActions();
    getAllSettings();
  }, [selectedOrg, auth.isLoggedIn]);
  useEffect(() => {
    const themeLink = document.getElementById("theme-link");
    if (themeLink && theme?.config) {
      themeLink.href = `${process.env.PUBLIC_URL}/themes/${theme.config}/theme.css`;
    }
  }, [theme]);
  useEffect(() => {
    if (language?.config) {
      i18n.changeLanguage(language?.config);
      localStorage.setItem('i18nextLng', language?.config);
    }
  }, [language])
  const menus = useMemo(() => {
    return useMenus(modules,t);
  }, [JSON.stringify(modules), t]);

  useEffect(() => {
    if (language?.config) {
      const { getLocaleForCalender } = useUtils();      
      addLocale(language?.config, getLocaleForCalender(language?.config));
      locale(language?.config);
    }
  }, [language?.config])

  const getAllSettings = () => {
    const allSettingsURL = "dashboard/get_all_settings";
    api
      .get(allSettingsURL)
      .then((res) => {
        const response = get(res, "data.data", []);
        setAllSettings(response);
        setTheme(response.find(setting => setting.setting_name === "theme"));
        setLanguage(response.find(setting => setting.setting_name === "language"));
      })
      .catch((e) => setAllSettings([]));
  }

  const getAllOrganisations = () => {
    const allOrgsURL = "org/get_all_orgs";
    api
      .get(allOrgsURL)
      .then((res) => setOrganisations(get(res, "data.data", [])))
      .catch((e) => setOrganisations([]));
  }

  const getAllModules = () => {
    setModulesInProgress(true);
    if (selectedOrg) {
      api
        .get(getModulesByOrgURL)
        .then((res) => {
          const sortedModules = res.data.data.sort((a, b) => {
            const orderA = a.module_order || Number.MAX_SAFE_INTEGER;
            const orderB = b.module_order || Number.MAX_SAFE_INTEGER;
            return orderA - orderB;
          });
          setModules(sortedModules);
          setModulesInProgress(false);
        })
        .catch((e) => {
          setModules([]);
          setModulesInProgress(false);
        });
    } else {
      const getModules = `modules/get_all_modules?orderby=module_order&ordertype=asc`;
      api
        .get(getModules)
        .then((res) => {
          setModules(get(res, "data.data", []));
          setModulesInProgress(false);
        })
        .catch((e) => {
          setModules([]);
          setModulesInProgress(false);
        });
    }
  }
  const getAllActions = () => {
    const getActionsURL = "actions/get_all_actions";
    api
      .get(getActionsURL)
      .then((res) => setActions(get(res, "data.data", [])))
      .catch((e) => setActions([]));
  };

  const metricTypes = useMemo(
    () => [
      {
        id: "JOURNEY_CHART_REALTIME",
        label: "Journey chart(realtime)",
      },
      {
        id: "METRIC_CARD",
        label: "Metric card",
      },
      {
        id: "METRIC_CARD_LITE",
        label: "Metric card lite",
      },
      {
        id: "METRIC_TABLE",
        label: "Metric table",
      },
      {
        id: "METRIC_CARD_ADVANCED",
        label: "Metric card advanced",
      },
      {
        id: "BAR_CHART",
        label: "Bar chart",
      },
      {
        id: "TIMESERIES_CHART",
        label: "Timeseries chart",
      },
      {
        id: "CALENDER_CHART",
        label: "Calendar chart",
      },
      {
        id: "LINE_CHART",
        label: "line chart",
      },
      {
        id: "AREA_CHART",
        label: "Area chart",
      },
      {
        id: "DONUT_CHART",
        label: "Donut chart",
      },
      {
        id: "FILTERS",
        label: "Filters",
      },
      {
        id: "PIE_DONUT_CHART",
        label: "Pie Donut Chart",
      },
    ],
    []
  );

  const navigate = useNavigate();
  useEffect(() => {
    const getRoles = `roles/get_roles`;
    api
      .get(getRoles)
      .then((res) => setRoles(get(res, "data.data", [])))
      .catch((e) => setRoles([]));
  }, []);

  const dialogProps = {
    seamless: false,
    position: "standard",
    persistent: false,
    maximized: false,
    closeBtn: false,
    width: "50vw",
    title: "...",
  };
  const drawerProps = {
    width: "50%",
  };

  const [dialogPageComponent, setDialogPageComponent] = useState(null);
  const [pageDialog, setPageDialog] = useState(false);
  const [pageDialogProps, setPageDialogProps] = useState(dialogProps);

  const [rightDrawer, setRightDrawer] = useState(false);
  const [rightDrawerProps, setRightDrawerProps] = useState(drawerProps);

  const [imageDialog, setImageDialog] = useState(false);
  const [imageDialogProps, setImageDialogProps] = useState({});
  const [errorDialog, setErrorDialog] = useState(false);
  const [pageErrors, setPageErrors] = useState([]);
  const [fullScreenLoading, setFullScreenLoading] = useState(false);
  const [fullScreenLoadingMsg, setFullScreenLoadingMsg] = useState(null);
  const [localeName, setLocaleName] = useState(null);
  const [pages, setPages] = useState({});

  function flashMsg(title, detail, type) {
    if (title || detail) {
      let severity = type || "success";
      toast.current.show({ severity, summary: title, detail, life: 3000 });
    }
  }

  function isDialogOpen() {
    if (pageDialog || rightDrawer) return true;
    return false;
  }
  function openPageDialog(page, props) {
    setDialogPageComponent(page);
    setPageDialogProps({ ...dialogProps, ...props });
    setPageDialog(true);
  }

  function openPageDrawer(page, props) {
    setDialogPageComponent(page);
    setRightDrawerProps({ ...drawerProps, ...props });
    setRightDrawer(true);
  }

  function openImageDialog(dialogProps) {
    setImageDialogProps(dialogProps);
    setImageDialog(true);
  }

  function closeDialogs() {
    setRightDrawer(false);
    setPageDialog(false);
    setImageDialog(false);
  }

  function showPageRequestError(error) {
    console.error(error);
    const defaultMsg = t("UNABLE_TO_SEND_REQUEST"); // if error is not a api request error.
    let errorMsgs = [defaultMsg];
    if (error?.request?.response) {
      let errorData = error.request.response;
      if (typeof errorData === "string") {
        try {
          // if error message is valid json data
          errorData = JSON.parse(errorData);
        } catch (ex) {
          //not a valid json. Display as simple message
          //console.error(ex);
        }
      }
      if (Array.isArray(errorData)) {
        errorMsgs = errorData;
      } else if (typeof errorData === "object") {
        errorMsgs = Object.values(errorData);
      } else {
        errorMsgs = [errorData.toString()];
      }
    }
    setPageErrors(errorMsgs);
    setErrorDialog(true);
  }

  function showAppLoading(msg) {
    if (msg) {
      setFullScreenLoadingMsg(msg);
      setFullScreenLoading(true);
    } else {
      setFullScreenLoadingMsg(null);
      setFullScreenLoading(false);
    }
  }

  function imagePreviewTemplate(imgSrc) {
    if (imgSrc) {
      return (
        <Image
          imageSize="large"
          preview={false}
          src={imgSrc}
          width="auto"
          height="auto"
          style={{ maxWidth: "100%", maxHeight: "60vh" }}
        />
      );
    }
  }

  function imageDialogTemplate() {
    const images = imageDialogProps.images || [];
    if (images.length > 1) {
      return (
        <div className="text-center">
          <Carousel
            style={{ width: "100%" }}
            value={images}
            itemTemplate={imagePreviewTemplate}
            circular={false}
            page={imageDialogProps.currentSlide}
          />
        </div>
      );
    } else if (images.length === 1) {
      return imagePreviewTemplate(imageDialogProps?.images[0]);
    }
  }
  function getImageDialogWidth() {
    const images = imageDialogProps.images || [];
    if (images.length > 1) {
      return "50vw";
    }
    return "auto";
  }

  function setAppLocale() {
    try {
      const localeName = i18n.locale;
      if (localeName) {
        let localeMessages = {};
        localeMessages = require(`i18n/locales/${localeName}.json`);

        //set prime react locale
        locale(localeName);
        updateLocaleOptions(localeMessages, localeName);

        setLocaleName(localeName);

        //build yup validations locale
        const yupLocales = {
          mixed: {
            default: i18n.t("validations.required"),
            required: i18n.t("validations.required"),
          },
          string: {
            email: i18n.t("validations.email"),
            ipAddress: i18n.t("validations.ipAddress"),
            min: ({ min }) => i18n.t("validations.minLength", { min }),
            max: ({ max }) => i18n.t("validations.maxLength", { max }),
          },
          number: {
            default: i18n.t("validations.numeric"),
            numeric: i18n.t("validations.numeric"),
            min: ({ min }) => i18n.t("validations.minValue", { min }),
            max: ({ max }) => i18n.t("validations.maxValue", { max }),
          },
        };
        //set yup locales
        setLocale(yupLocales);
      }
    } catch (err) {
      console.error(err);
    }
  }

  function setPageData(page, pageData) {
    const currentPage = pages[page];
    if (currentPage) {
      currentPage.pageData = pageData;
    } else {
      pages[page] = {
        pageData,
      };
    }
    setPages(pages);
  }

  function getPageData(page) {
    const currentPage = pages[page];
    if (currentPage) {
      return currentPage.pageData;
    }
    return null;
  }

  function setPageFormData(page, formData) {
    const currentPage = pages[page];
    if (currentPage) {
      currentPage.formData = formData;
    } else {
      pages[page] = {
        formData,
      };
    }
    setPages(pages);
  }

  function getPageFormData(page) {
    const currentPage = pages[page];
    if (currentPage) {
      return currentPage.formData;
    }
    return {};
  }

  useEffect(() => {
    setAppLocale();
  }, []);

  return (
    <AppContext.Provider
      value={{
        menus,
        roles,
        selectedOrg,
        metricTypes,
        setSelectedOrg,
        modules,
        metricFilterParameters,
        setMetricFilterParameters,
        modulesInProgress,
        organisations,
        roles,
        licenseData,
        actions,
        getModulesByOrgURL,
        localeName,
        navigate,
        flashMsg,
        isDialogOpen,
        openPageDialog,
        openPageDrawer,
        openImageDialog,
        closeDialogs,
        showPageRequestError,
        setLicenseData,
        showAppLoading,
        setFullScreenLoading,
        setPageData,
        getPageData,
        setPageFormData,
        getPageFormData,  
        setEventFilterParameters,
        setActionFilterParameters,
        setUserFilterParameters,
        userFilterParameters,
        actionFilterParameters,
        eventFilterParameters,    
        getAllOrganisations,
        getAllModules,
        getAllActions,
        setAllSettings,
        setTheme,
        theme,
        allSettings,
        getAllSettings,
        setLanguage,
        language
      }}
    >
      {children}
      <Toast ref={toast} position="top-center"></Toast>
      <link
        id="theme-link"
        rel="stylesheet"
        href={`${process.env.PUBLIC_URL}/themes/${theme?.config}/theme.css`}
      />
      <ConfirmDialog />

      <Sidebar
        breakpoints={{ "960px": "40vw", "640px": "95vw" }}
        visible={rightDrawer}
        position="right"
        onHide={() => setRightDrawer(false)}
        style={{ width: rightDrawerProps.width }}
      >
        {dialogPageComponent}
      </Sidebar>

      <Dialog
        breakpoints={{ "960px": "40vw", "640px": "95vw" }}
        className="card py-4 px-2"
        position={pageDialogProps.position}
        modal={!pageDialogProps.persistent}
        draggable={false}
        // dismissableMask={!pageDialogProps.seamless}
        visible={pageDialog}
        onHide={() => setPageDialog(false)}
        style={{ width: pageDialogProps.width }}
        showHeader={false}
      >
        {pageDialogProps.closeBtn && (
          <Button
            onClick={() => setPageDialog(false)}
            style={{ position: "absolute", right: "25px", top: "15px" }}
            icon="pi pi-times"
            className="p-button-rounded p-button-text p-button-plain"
          />
        )}
        {dialogPageComponent}
      </Dialog>

      {/* <!-- image preview dialog--> */}
      <Dialog
        header="..."
        showHeader={true}
        breakpoints={{ "960px": "40vw", "640px": "95vw" }}
        style={{ width: getImageDialogWidth() }}
        visible={imageDialog}
        dismissableMask
        modal={true}
        onHide={() => setImageDialog(false)}
      >
        {imageDialogTemplate()}
      </Dialog>

      {/* request error dialog */}
      <Dialog
        footer="..."
        header={
          <div className="flex align-items-center">
            <div className="mr-2">
              <Avatar
                className="bg-pink-100 text-pink-600"
                icon="pi pi-exclamation-triangle"
              />
            </div>
            <div className="flex text-lg text-pink-600 font-bold">
          {t("UNABLE_TO_COMPLETE_REQUEST")}
            </div>
          </div>
        }
        modal
        visible={errorDialog}
        breakpoints={{ "960px": "50vw", "640px": "95vw" }}
        style={{ width: "30vw" }}
        position="top"
        onHide={() => setErrorDialog(false)}
        draggable={false}
      >
        {pageErrors?.map((msg) => (
          <div key={msg} className="text-grey-500 font-bold">
            {msg}
          </div>
        ))}
      </Dialog>

      <Dialog
        className="card m-0 text-center"
        showHeader={false}
        modal
        visible={fullScreenLoading}
        breakpoints="{ '960px': '50vw', '640px': '95vw' }"
        style={{ width: "10vw" }}
        position="center"
        onHide={() => setFullScreenLoading(false)}
      >
        <ProgressSpinner style={{ width: "40px" }} />
        {fullScreenLoadingMsg}
      </Dialog>
    </AppContext.Provider>
  );
};
